import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { DataTable, type TanstackReactTable, confirm, toast } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { PendingFitmentSkuRow } from "../api/client";

type CatalogPendingFitmentTableProps = {
  isLoading: boolean;
  data: PendingFitmentSkuRow[];
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onReject?: (catalogSkuId: string) => Promise<void>;
  canReject?: boolean;
};

export function CatalogPendingFitmentTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onReject,
  canReject = false,
}: CatalogPendingFitmentTableProps) {
  const columns: TanstackReactTable.ColumnDef<PendingFitmentSkuRow>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    {
      id: "productName",
      header: "ຊື່ສິນຄ້າ",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.productName}</span>
      ),
      size: 260,
    },
    {
      id: "oemPartNo",
      header: "OEM",
      cell: ({ row }) => row.original.oemPartNo ?? "—",
      size: 120,
    },
    {
      id: "updatedAt",
      header: "ອັບເດດລ່າສຸດ",
      cell: ({ row }) => formatDateTimeLocal(row.original.updatedAt),
      size: 140,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Link
            to="/app/catalog/$id"
            params={{ id: row.original.catalogProductId }}
            className="text-primary text-sm font-medium hover:underline"
          >
            ເພີ່ມ fitment
          </Link>
          {canReject && onReject ? (
            <button
              type="button"
              className="text-destructive text-sm hover:underline"
              onClick={async () => {
                const ok = await confirm({
                  title: "ປະຕິເສດສິນຄ້າຈາກຮ້ານ",
                  description:
                    "ຈະ archive catalog ແລະ ປິດ SKU ຂອງຮ້ານ — ບໍ່ສາມາດ publish marketplace ໄດ້",
                  actionText: "ປະຕິເສດ",
                  ActionProps: { variant: "destructive" },
                });
                if (!ok) return;
                try {
                  await onReject(row.original.id);
                  toast.success("ປະຕິເສດສິນຄ້າແລ້ວ");
                } catch {
                  toast.error("ປະຕິເສດລົ້ມເຫຼວ");
                }
              }}
            >
              ປະຕິເສດ
            </button>
          ) : null}
        </div>
      ),
      size: 180,
    },
  ];

  return (
    <DataTable<PendingFitmentSkuRow, unknown>
      noDataMessage="ບໍ່ມີ SKU ລໍຖ້າ fitment"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
